import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Flame, MessageSquareReply, CalendarClock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/components/common";
import { followUpLink } from "@/components/RepliesPanel";

function List({ icon: Icon, title, items, render, tone }) {
  if (!items.length) return null;
  return (
    <div>
      <p className={`mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${tone}`}><Icon className="h-3.5 w-3.5" />{title} ({items.length})</p>
      <div className="space-y-1">{items.slice(0, 5).map(render)}</div>
      {items.length > 5 && <p className="mt-1 text-xs text-slate-400">+{items.length - 5} more</p>}
    </div>
  );
}

const name = (c) => c.full_name || c.company || "Contact";

export default function DigestCard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/digest").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const empty = !d.totals.hot && !d.totals.replies && !d.totals.followups_due;
  return (
    <section className="cd-card border-teal-200 bg-gradient-to-br from-white to-teal-50/40" data-testid="digest-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold"><Sun className="h-5 w-5 text-amber-500" />{greet} — your lead digest</h2>
        <p className="text-xs text-slate-500" data-testid="digest-totals">{d.totals.hot} hot · {d.totals.replies} new replies (24h) · {d.totals.followups_due} follow-ups due</p>
      </div>
      {empty && <p className="text-sm text-slate-500">Nothing urgent. Mark contacts as Hot and set follow-up dates to see them here.</p>}
      <div className="space-y-4">
        {d.groups.map((g) => (
          <div key={g.event?.id || "none"} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`digest-group-${g.event?.id || "none"}`}>
            <p className="mb-2 font-bold">{g.event ? g.event.name : "No event"}</p>
            <div className="grid gap-3 md:grid-cols-3">
              <List icon={MessageSquareReply} title="New replies" tone="text-green-700" items={g.replies} render={(r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 text-sm">
                  <Link to={`/contacts/${r.contact_id}`} className="min-w-0"><p className="truncate font-semibold">{r.contact_name || r.from}</p><p className="truncate text-xs text-slate-500">{r.subject} · {fmtDate(r.received_at)}</p></Link>
                  <Link to={followUpLink(r.contact_id, r)} title="Draft follow-up" data-testid={`digest-followup-${r.id}`} className="shrink-0 rounded-lg bg-teal-50 p-1.5 text-teal-700"><Sparkles className="h-4 w-4" /></Link>
                </div>
              )} />
              <List icon={Flame} title="Hot contacts" tone="text-rose-600" items={g.hot} render={(c) => (
                <Link key={c.id} to={`/contacts/${c.id}`} className="block text-sm"><p className="truncate font-semibold">{name(c)}</p><p className="truncate text-xs text-slate-500">{c.next_action || c.company || ""}</p></Link>
              )} />
              <List icon={CalendarClock} title="Follow-ups due" tone="text-amber-700" items={g.followups_due} render={(c) => (
                <Link key={c.id} to={`/contacts/${c.id}`} className="block text-sm"><p className="truncate font-semibold">{name(c)}</p><p className="truncate text-xs text-slate-500">Due {c.follow_up_date}{c.next_action ? ` · ${c.next_action}` : ""}</p></Link>
              )} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
